import {
  Alert,
  Button,
  FieldError,
  Fieldset,
  Input,
  Label,
  Spinner,
  TextField,
  Typography,
} from "@heroui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import { OnboardingFormData, onboardingSchema } from "./schema";

import {
  DEFAULT_ORGANIZATION_TYPE,
  ORGANIZATION_NAME_FIELD,
} from "@/constants/organization";
import {
  clearPendingOrganization,
  readPendingOrganization,
} from "@/features/onboarding/helpers/pending-organization";
import { getDetectedTimezone } from "@/utils/date";
import { useCreateOrganization } from "@/hooks/api/organization/useCreateOrganization";
import {
  getDefaultCurrencyFromTimezone,
  getDefaultRegionFromTimezone,
} from "@/utils/organization";
import { useOpenOrganization } from "@/hooks/use-open-organization";

export interface OnboardingFormProps {
  isSetupPreview?: boolean;
}

// The name is the only question left: the type comes from the build's slug and
// the locale from detection, both editable later in settings.
const DETECTED_TIMEZONE = getDetectedTimezone();
const SETUP_PREVIEW_ORGANIZATION_NAME = "Harbor & Pine";
const ORGANIZATION_DEFAULTS = {
  type: DEFAULT_ORGANIZATION_TYPE,
  timezone: DETECTED_TIMEZONE,
  region: getDefaultRegionFromTimezone(DETECTED_TIMEZONE),
  currency: getDefaultCurrencyFromTimezone(DETECTED_TIMEZONE),
};

export const OnboardingForm = ({
  isSetupPreview = false,
}: OnboardingFormProps) => {
  const hasAutoCreated = useRef(false);

  const openOrganization = useOpenOrganization();
  const { mutate, isPending, isError, error } = useCreateOrganization();

  // The name is usually already answered before sign-up, so the usual path is
  // to create the workspace and never render.
  const pendingOrganization = useMemo(
    () =>
      isSetupPreview
        ? { name: SETUP_PREVIEW_ORGANIZATION_NAME }
        : readPendingOrganization(),
    [isSetupPreview],
  );

  const [isAutoCreating, setIsAutoCreating] = useState(
    Boolean(pendingOrganization),
  );

  const form = useForm<OnboardingFormData>({
    mode: "all",
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      organizationName: pendingOrganization?.name ?? "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = form;

  const createOrganization = useCallback(
    (name: string, onError?: () => void) => {
      mutate(
        { name, ...ORGANIZATION_DEFAULTS },
        {
          onSuccess: async ({ externalId }) => {
            // Someone who already belongs to organizations would otherwise boot
            // back into the active one instead of the one they just made.
            if (externalId) await openOrganization(externalId);
            else window.location.href = "/dashboard";
            window.location.reload();
          },
          onError,
        },
      );
    },
    [mutate, openOrganization],
  );

  useEffect(() => {
    if (!pendingOrganization || hasAutoCreated.current || isSetupPreview) {
      return;
    }

    hasAutoCreated.current = true;
    clearPendingOrganization();

    // Fall through to the form rather than stranding them on a spinner.
    createOrganization(pendingOrganization.name, () =>
      setIsAutoCreating(false),
    );
  }, [pendingOrganization, createOrganization, isSetupPreview]);

  if (isAutoCreating) {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <Spinner size="lg" />
        <Typography color="muted" type="body-sm">
          Setting up {pendingOrganization?.name}...
        </Typography>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <form
        className="flex flex-col w-full gap-4"
        onSubmit={handleSubmit((data) =>
          createOrganization(data.organizationName),
        )}
      >
        {isError && (
          <Alert status="danger">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>{error.message}</Alert.Title>
            </Alert.Content>
          </Alert>
        )}

        <Fieldset className="w-full gap-3">
          <Fieldset.Group className="w-full space-y-3">
            <Controller
              control={control}
              name="organizationName"
              render={({ field, fieldState }) => (
                <TextField
                  isRequired
                  isInvalid={!!fieldState.error}
                  name={field.name}
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  onChange={field.onChange}
                >
                  <Label>{ORGANIZATION_NAME_FIELD.label}</Label>
                  <Input
                    ref={field.ref}
                    autoComplete="organization"
                    placeholder={ORGANIZATION_NAME_FIELD.placeholder}
                    variant="secondary"
                  />
                  <FieldError>{errors.organizationName?.message}</FieldError>
                </TextField>
              )}
            />
          </Fieldset.Group>

          <Fieldset.Actions className="w-full flex-col items-stretch pt-0">
            <Button
              className="mt-2 w-full"
              isDisabled={!isValid}
              isPending={isPending}
              type="submit"
              variant="primary"
            >
              {({ isPending }) => (
                <>
                  {isPending && <Spinner color="current" size="sm" />}
                  <Typography type="body-sm" weight="medium">
                    Continue
                  </Typography>
                </>
              )}
            </Button>
          </Fieldset.Actions>
        </Fieldset>
      </form>
    </FormProvider>
  );
};
